import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  FileText,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  Search,
  Shield,
  ClipboardList,
  Lightbulb,
  Award,
  Heart,
  CheckCircle,
  AlertCircle,
  Info,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dda65bda-4395-4219-89e9-934b3558952e/artifacts/kq5kdno6_WhatsApp%20Image%202026-02-19%20at%2023.17.23.jpeg";

// Navigation items with role-based visibility
const navItems = [
  {
    title: 'Gösterge Paneli',
    href: '/dashboard',
    icon: LayoutDashboard,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher', 'intervention_specialist', 'counselor'],
  },
  {
    title: 'Öğrenciler',
    href: '/students',
    icon: GraduationCap,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher', 'intervention_specialist', 'counselor'],
  },
  {
    title: 'Sınıflar',
    href: '/classes',
    icon: Users,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher'],
  },
  {
    title: 'Tarama Dönemleri',
    href: '/screenings',
    icon: ClipboardList,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher'],
  },
  {
    title: 'Kaynak Kütüphanesi',
    href: '/resources',
    icon: BookOpen,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher', 'intervention_specialist'],
  },
  {
    title: 'İyi Örnekler',
    href: '/cases',
    icon: Lightbulb,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher'],
  },
  {
    title: 'Aile Portalı',
    href: '/parent',
    icon: Heart,
    roles: ['parent'],
  },
  {
    title: 'Veli Katılımı',
    href: '/parent-engagement',
    icon: Heart,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher'],
  },
  {
    title: 'Duyurular',
    href: '/announcements',
    icon: MessageSquare,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher', 'intervention_specialist', 'counselor', 'parent'],
  },
  {
    title: 'Mesajlar',
    href: '/messages',
    icon: MessageSquare,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher', 'intervention_specialist', 'counselor'],
  },
  {
    title: 'Koçluk & Akademi',
    href: '/coaching',
    icon: Award,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher'],
  },
  {
    title: 'Raporlar',
    href: '/reports',
    icon: FileText,
    roles: ['tenant_owner', 'center_director', 'reading_coach', 'teacher'],
  },
];

const adminItems = [
  {
    title: 'Kullanıcı Yönetimi',
    href: '/admin/users',
    icon: Users,
    roles: ['tenant_owner', 'center_director'],
  },
  {
    title: 'Kurum Ayarları',
    href: '/admin/settings',
    icon: Settings,
    roles: ['tenant_owner'],
  },
  {
    title: 'Denetim Kayıtları',
    href: '/admin/audit-logs',
    icon: Shield,
    roles: ['tenant_owner', 'center_director'],
  },
];

const superadminItems = [
  {
    title: 'Süper Admin Paneli',
    href: '/superadmin',
    icon: Shield,
    roles: ['superadmin'],
  },
];

// Role display names in Turkish
const roleNames = {
  superadmin: 'Süper Admin',
  tenant_owner: 'Kurum Sahibi',
  center_director: 'Merkez Müdürü',
  reading_coach: 'Okuma Koçu',
  teacher: 'Öğretmen',
  intervention_specialist: 'Müdahale Uzmanı',
  counselor: 'Rehber Öğretmen',
  parent: 'Veli',
  student: 'Öğrenci',
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const isSuperadmin = user?.role === 'superadmin' || user?.email === 'kayhankarli@gmail.com';
  
  // Debug superadmin check

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/communications/announcements?limit=5`, {
          credentials: 'include',
          headers
        });
        
        if (response.ok) {
          const data = await response.json();
          // Convert announcements to notifications format (response is array directly)
          const announcements = Array.isArray(data) ? data : (data.announcements || []);
          const notifs = announcements.slice(0, 5).map(a => ({
            id: a.announcement_id,
            title: a.title,
            message: a.content?.substring(0, 100) + '...',
            type: a.priority === 'urgent' ? 'warning' : a.priority === 'high' ? 'info' : 'success',
            time: new Date(a.created_at).toLocaleDateString('tr-TR'),
            read: false
          }));
          setNotifications(notifs);
          setUnreadCount(notifs.length);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        // Set demo notifications on error
        setNotifications([
          { id: 1, title: 'Yeni Duyuru', message: 'Sistem güncellemesi yapıldı', type: 'info', time: 'Bugün', read: false },
          { id: 2, title: 'Tarama Hatırlatması', message: 'Bu hafta tarama yapılması gereken öğrenciler var', type: 'warning', time: 'Dün', read: false },
          { id: 3, title: 'Yeni Kaynak', message: 'Kaynak kütüphanesine yeni materyal eklendi', type: 'success', time: '2 gün önce', read: true }
        ]);
        setUnreadCount(2);
      }
    };
    
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  // Filter nav items - superadmin sees all except parent-only pages
  const filteredNavItems = navItems.filter(item => {
    if (item.roles.includes(user?.role)) return true;
    if (isSuperadmin) {
      // Don't show parent-only pages to superadmin (they won't work without parent profile)
      if (item.roles.length === 1 && item.roles[0] === 'parent') return false;
      return item.roles.length > 0;
    }
    return false;
  });

  const filteredAdminItems = adminItems.filter(item =>
    item.roles.includes(user?.role) || isSuperadmin
  );

  const filteredSuperadminItems = superadminItems.filter(item =>
    isSuperadmin
  );
  

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.href || 
                     location.pathname.startsWith(item.href + '/');
    
    return (
      <Link
        to={item.href}
        onClick={() => mobile && setSidebarOpen(false)}
        data-testid={`nav-${item.href.replace(/\//g, '-').slice(1)}`}
        className={`
          flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium
          transition-colors duration-200
          ${isActive 
            ? 'bg-white/10 text-secondary' 
            : 'text-white/80 hover:bg-white/5 hover:text-white'
          }
        `}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        <span className={mobile ? '' : 'hidden lg:inline'}>{item.title}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-primary-900
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="flex-shrink-0 flex items-center justify-between h-16 px-4 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="BilgeOkur" 
              className="h-12 w-auto object-contain"
            />
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/80 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
          <div className="space-y-1">
            {filteredNavItems.map(item => (
              <NavLink key={item.href} item={item} mobile />
            ))}
          </div>

          {filteredAdminItems.length > 0 && (
            <>
              <div className="mt-6 mb-2 px-3">
                <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  Yönetim
                </span>
              </div>
              <div className="space-y-1">
                {filteredAdminItems.map(item => (
                  <NavLink key={item.href} item={item} mobile />
                ))}
              </div>
            </>
          )}

          {filteredSuperadminItems.length > 0 && (
            <>
              <div className="mt-6 mb-2 px-3">
                <span className="text-xs font-semibold text-secondary uppercase tracking-wider">
                  Süper Admin
                </span>
              </div>
              <div className="space-y-1">
                {filteredSuperadminItems.map(item => (
                  <NavLink key={item.href} item={item} mobile />
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User info in sidebar (mobile) */}
        <div className="p-4 border-t border-white/10 lg:hidden">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-secondary text-primary-900 font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{roleNames[user?.role]}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 px-4 lg:px-6">
          <div className="flex items-center justify-between h-full gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Öğrenci, kaynak veya duyuru ara..."
                  className="pl-10 bg-slate-50 border-slate-200"
                  data-testid="global-search"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="relative"
                    data-testid="notifications-btn"
                  >
                    <Bell className="h-5 w-5 text-slate-600" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h4 className="font-semibold text-sm">Bildirimler</h4>
                    {unreadCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs text-primary hover:text-primary/80"
                        onClick={markAllAsRead}
                      >
                        Tümünü okundu işaretle
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                        Bildirim bulunmuyor
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div 
                          key={notif.id}
                          className={`px-4 py-3 border-b last:border-0 hover:bg-slate-50 cursor-pointer ${!notif.read ? 'bg-blue-50/50' : ''}`}
                          onClick={() => navigate('/announcements')}
                        >
                          <div className="flex gap-3">
                            <div className="mt-0.5">
                              {getNotificationIcon(notif.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'font-semibold' : 'font-medium'} text-slate-900`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {notif.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {notif.time}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-2 border-t">
                    <Button 
                      variant="ghost" 
                      className="w-full text-sm text-primary"
                      onClick={() => navigate('/announcements')}
                    >
                      Tüm bildirimleri gör
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 px-2"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-primary text-white text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{roleNames[user?.role]}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400 hidden md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Profil Ayarları
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600"
                    data-testid="logout-btn"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 lg:hidden">
        <div className="flex items-center justify-around h-16">
          {filteredNavItems.slice(0, 5).map(item => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 h-full
                  ${isActive ? 'text-primary' : 'text-slate-500'}
                `}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-[10px] mt-1">{item.title.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
